import React, { useRef, useEffect, useState } from 'react';
import { DiffEditor, Monaco } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import { ChevronUp, ChevronDown, GitCompare, FileCode } from 'lucide-react';

interface DiffChange {
  type: 'add' | 'remove' | 'unchanged';
  lineNumber: number;
  content: string;
}

interface CodeDiff {
  version1: string;
  version2: string;
  additions: number;
  deletions: number;
  changes: DiffChange[];
}

interface VersionDiffViewerProps {
  originalCode: string;
  modifiedCode: string;
  originalVersion: string;
  modifiedVersion: string;
  diffStats?: CodeDiff;
  height?: string;
  onNavigateToChange?: (lineNumber: number) => void;
}

export const VersionDiffViewer: React.FC<VersionDiffViewerProps> = ({
  originalCode,
  modifiedCode,
  originalVersion,
  modifiedVersion,
  diffStats,
  height = '600px',
  onNavigateToChange,
}) => {
  const diffEditorRef = useRef<editor.IStandaloneDiffEditor | null>(null);
  const [currentChangeIndex, setCurrentChangeIndex] = useState(0);
  const [changePositions, setChangePositions] = useState<number[]>([]);

  const handleEditorDidMount = (editor: editor.IStandaloneDiffEditor, monaco: Monaco) => {
    diffEditorRef.current = editor;

    // Register Solidity language if not already registered
    if (!monaco.languages.getLanguages().some((lang: { id: string }) => lang.id === 'solidity')) {
      registerSolidityLanguage(monaco);
    }

    // Get change positions for navigation
    setTimeout(() => {
      const lineChanges = editor.getLineChanges();
      if (lineChanges) {
        const positions = lineChanges.map(change => change.modifiedStartLineNumber);
        setChangePositions(positions);
      }
    }, 500);
  };

  const registerSolidityLanguage = (monaco: Monaco) => {
    monaco.languages.register({ id: 'solidity' });

    monaco.languages.setMonarchTokensProvider('solidity', {
      keywords: [
        'pragma', 'solidity', 'import', 'contract', 'interface', 'library', 'abstract',
        'function', 'modifier', 'event', 'struct', 'enum', 'mapping', 'constructor',
        'public', 'private', 'internal', 'external', 'pure', 'view', 'payable',
        'constant', 'immutable', 'virtual', 'override', 'returns', 'return',
        'if', 'else', 'for', 'while', 'do', 'break', 'continue', 'throw',
        'emit', 'require', 'assert', 'revert', 'try', 'catch',
        'new', 'delete', 'this', 'super', 'selfdestruct',
        'true', 'false', 'memory', 'storage', 'calldata',
      ],
      typeKeywords: [
        'address', 'bool', 'string', 'bytes', 'uint256', 'uint8', 'uint128',
        'int256', 'int8', 'int128', 'bytes32', 'bytes4',
      ],
      operators: ['=', '>', '<', '!', '~', '?', ':', '==', '<=', '>=', '!=', '&&', '||', '++', '--', '+', '-', '*', '/', '&', '|', '^', '%'],
      symbols: /[=><!~?:&|+\-*\/\^%]+/,

      tokenizer: {
        root: [
          [/\/\/.*$/, 'comment'],
          [/\/\*/, 'comment', '@comment'],
          [/"([^"\\]|\\.)*$/, 'string.invalid'],
          [/"/, 'string', '@string'],
          [/\d*\.\d+([eE][\-+]?\d+)?/, 'number.float'],
          [/0[xX][0-9a-fA-F]+/, 'number.hex'],
          [/\d+/, 'number'],
          [/[a-zA-Z_]\w*/, {
            cases: {
              '@typeKeywords': 'type',
              '@keywords': 'keyword',
              '@default': 'identifier',
            },
          }],
          [/@symbols/, { cases: { '@operators': 'operator', '@default': '' } }],
          [/[{}()\[\]]/, '@brackets'],
          [/[;,.]/, 'delimiter'],
        ],
        comment: [
          [/[^\/*]+/, 'comment'],
          [/\*\//, 'comment', '@pop'],
          [/[\/*]/, 'comment'],
        ],
        string: [
          [/[^\\"]+/, 'string'],
          [/"/, 'string', '@pop'],
        ],
      },
    });
  };


  const navigateToPreviousChange = () => {
    if (changePositions.length === 0 || !diffEditorRef.current) return;
    
    const newIndex = currentChangeIndex > 0 ? currentChangeIndex - 1 : changePositions.length - 1;
    setCurrentChangeIndex(newIndex);
    
    const lineNumber = changePositions[newIndex];
    diffEditorRef.current.getModifiedEditor().revealLineInCenter(lineNumber);
    onNavigateToChange?.(lineNumber);
  };

  const navigateToNextChange = () => {
    if (changePositions.length === 0 || !diffEditorRef.current) return;
    
    const newIndex = currentChangeIndex < changePositions.length - 1 ? currentChangeIndex + 1 : 0;
    setCurrentChangeIndex(newIndex);
    
    const lineNumber = changePositions[newIndex];
    diffEditorRef.current.getModifiedEditor().revealLineInCenter(lineNumber);
    onNavigateToChange?.(lineNumber);
  };

  return (
    <div className="flex flex-col rounded-lg overflow-hidden border border-surface-700 bg-surface-900">
      {/* Header with version info and navigation */}
      <div className="flex items-center justify-between px-4 py-3 bg-surface-800 border-b border-surface-700">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <GitCompare className="w-5 h-5 text-primary-400" />
            <span className="text-sm font-medium text-surface-200">Comparação de Versões</span>
          </div>
          
          <div className="flex items-center gap-2 text-sm">
            <span className="px-2 py-1 rounded bg-red-500/20 text-red-400 font-mono">
              v{originalVersion}
            </span>
            <span className="text-surface-500">→</span>
            <span className="px-2 py-1 rounded bg-green-500/20 text-green-400 font-mono">
              v{modifiedVersion}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Stats */}
          {diffStats && (
            <div className="flex items-center gap-3 text-sm">
              <span className="text-green-400">+{diffStats.additions} adições</span>
              <span className="text-red-400">-{diffStats.deletions} remoções</span>
            </div>
          )}

          {/* Navigation buttons */}
          {changePositions.length > 0 && (
            <div className="flex items-center gap-1">
              <span className="text-xs text-surface-400 mr-2">
                {currentChangeIndex + 1} / {changePositions.length}
              </span>
              <button
                onClick={navigateToPreviousChange}
                className="p-1.5 rounded hover:bg-surface-700 text-surface-400 hover:text-surface-200 transition-colors"
                title="Alteração anterior"
              >
                <ChevronUp className="w-4 h-4" />
              </button>
              <button
                onClick={navigateToNextChange}
                className="p-1.5 rounded hover:bg-surface-700 text-surface-400 hover:text-surface-200 transition-colors"
                title="Próxima alteração"
              >
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>


      {/* Version labels */}
      <div className="flex border-b border-surface-700">
        <div className="flex-1 px-4 py-2 bg-red-500/10 border-r border-surface-700">
          <div className="flex items-center gap-2 text-sm text-red-400">
            <FileCode className="w-4 h-4" />
            <span>Versão Anterior (v{originalVersion})</span>
          </div>
        </div>
        <div className="flex-1 px-4 py-2 bg-green-500/10">
          <div className="flex items-center gap-2 text-sm text-green-400">
            <FileCode className="w-4 h-4" />
            <span>Versão Atual (v{modifiedVersion})</span>
          </div>
        </div>
      </div>

      {/* Diff Editor */}
      <DiffEditor
        height={height}
        language="solidity"
        original={originalCode}
        modified={modifiedCode}
        theme="vs-dark"
        onMount={handleEditorDidMount}
        options={{
          readOnly: true,
          renderSideBySide: true,
          enableSplitViewResizing: true,
          originalEditable: false,
          scrollBeyondLastLine: false,
          fontSize: 13,
          fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
          minimap: { enabled: false },
          lineNumbers: 'on',
          folding: true,
          wordWrap: 'on',
          automaticLayout: true,
          renderIndicators: true,
          ignoreTrimWhitespace: false,
          diffWordWrap: 'on',
          scrollbar: {
            vertical: 'auto',
            horizontal: 'auto',
          },
          padding: {
            top: 10,
            bottom: 10,
          },
        }}
      />

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 px-4 py-2 bg-surface-800 border-t border-surface-700 text-xs text-surface-400">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-green-500/30 border border-green-500"></div>
          <span>Linhas adicionadas</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-red-500/30 border border-red-500"></div>
          <span>Linhas removidas</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-yellow-500/30 border border-yellow-500"></div>
          <span>Linhas modificadas</span>
        </div>
      </div>
    </div>
  );
};

export default VersionDiffViewer;
